import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  type SetReturnLocationsState,
  SPECIAL_LOCATION_ID,
} from "@redotech/redo-return-core-common/return-flow-step-valid";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { LocationOptionsContext } from "../flows/location-options-context";
import { LocationCard } from "./location-card";
import { RateSourceSelection } from "./rate-source-selection/rate-source-selection";
import { useLocationSelectionHandler } from "./use-location-selection-handler";

export const SetReturnLocationsDetails = memo(
  function SetReturnLocationsDetails({
    state,
    setState,
  }: StepTypeDetailsProps<SetReturnLocationsState>) {
    const { t } = useTranslation();
    const locationOptions = useContext(LocationOptionsContext);
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

    const resolvedLocations = useLoad(
      async (signal) => {
        const addressIds = state.locations.filter(
          (id) =>
            id !== SPECIAL_LOCATION_ID.ORIGINAL &&
            id !== SPECIAL_LOCATION_ID.SHIPMENT_ORIGIN &&
            id !== SPECIAL_LOCATION_ID.REDO_MANAGED &&
            id !== "",
        );

        if (addressIds.length === 0) {
          return { addresses: [], externalMap: new Map<string, string>() };
        }

        const result = await rpcClient.getAddressesByIds(
          { ids: addressIds },
          { signal },
        );

        const externalMap = new Map<string, string>();
        for (const addr of result.addresses) {
          if (addr.type !== "custom" && addr.externalId) {
            externalMap.set(addr._id.toString(), addr.externalId);
          }
        }

        return { addresses: result.addresses, externalMap };
      },
      [state.locations, rpcClient],
    );

    const handleAddLocation = () => {
      setState((prev) => ({ ...prev, locations: [...prev.locations, ""] }));
    };

    const handleDeleteLocation = (addressId: string) => {
      setState((prev) => ({
        ...prev,
        locations: prev.locations.filter((id) => id !== addressId),
      }));
    };

    const handleLocationSelect = useLocationSelectionHandler(setState);

    const handleRestockLocationSelect = (
      addressId: string,
      restockLocationId: string | null,
    ) => {
      setState((prev) => ({
        ...prev,
        restockExternalLocationIds: {
          ...prev.restockExternalLocationIds,
          [addressId]: restockLocationId,
        },
      }));
    };
    if (!locationOptions) {
      return null;
    }
    return (
      <Flex dir="column" gap="md">
        <Text fontSize="xs" textColor="secondary">
          {t(
            "The actual shipping location will be determined by the cheapest available shipping rate.",
          )}
        </Text>
        <Flex dir="column" gap="3xl">
          {state.locations.map((locationId, index) => (
            <LocationCard
              addressId={locationId}
              externalLocationId={resolvedLocations.value?.externalMap.get(
                locationId,
              )}
              index={index}
              key={`${locationId}-${index}`}
              locationOptions={locationOptions}
              onDelete={handleDeleteLocation}
              onLocationSelect={(newAddressId, newExternalLocationId) =>
                handleLocationSelect(
                  locationId,
                  newAddressId,
                  newExternalLocationId,
                  index,
                )
              }
              onRestockLocationSelect={handleRestockLocationSelect}
              resolvedAddresses={resolvedLocations.value?.addresses ?? []}
              restockExternalLocationId={
                state.restockExternalLocationIds?.[locationId]
              }
              selectedLocations={state.locations}
            />
          ))}
        </Flex>
        <Flex justify="flex-start" mt="2xl">
          <RedoButton
            hierarchy="secondary"
            IconLeading={() => <>+</>}
            onClick={handleAddLocation}
            size="xs"
            text={t("Add location")}
          />
        </Flex>
        <RateSourceSelection
          rateSourceSelection={state.rateSourceSelection}
          updateRateSourceSelection={(rateSourceSelection) => {
            setState((prev) => ({ ...prev, rateSourceSelection }));
          }}
        />
      </Flex>
    );
  },
);
